import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/images/namelogo.png";
import axiosInstance from "../../utils/api";
import { FiEye, FiEyeOff } from "react-icons/fi";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await axiosInstance.post("/auth/login", { email, password });

      const accessToken = res?.data?.accessToken;
      const refreshToken = res?.data?.refreshToken;
      
      const userRole = res?.data?.user?.role || res?.data?.role;
      const normalizedRole =
        typeof userRole === "string" ? userRole.toLowerCase() : "";

      if (normalizedRole !== "super") {
        localStorage.removeItem("token");
        throw new Error("Invalid credentials");
      }

      localStorage.setItem("token", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      navigate("/dashboard");
    } catch (err) {
      if (err?.response?.status === 429) {
        setError("Too many login attempts. Please wait and try again.");
      } else if (err?.response?.status === 401) {
        setError("Invalid email or password");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 px-4 py-10">

      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-blue-600/10 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-slate-600/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
      </div>

      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b15_1px,transparent_1px),linear-gradient(to_bottom,#1e293b15_1px,transparent_1px)] bg-[size:4rem_4rem]" />

      <div className="relative z-10 flex items-center justify-center min-h-screen">
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-16">

          <div className="flex items-center justify-center">
            <div className="text-center px-6 lg:px-10">
              <div className="inline-flex items-center justify-center mb-8 rounded-3xl bg-white/95 border border-slate-200 px-6 py-3 shadow-sm">
                <img src={logo} alt="Logo" className="w-40" />
              </div>
              <p className="text-sm uppercase tracking-[0.25em] text-slate-400">
                Admin Console
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <form
              onSubmit={handleSubmit}
              className="w-full max-w-md bg-white border border-slate-200 rounded-3xl shadow-2xl px-8 py-10"
            >
              <h2 className="text-2xl font-semibold text-slate-900 mb-1">
                Welcome back
              </h2>
              <p className="text-sm text-slate-500 mb-8">
                Use your admin credentials to continue.
              </p>

              {error && (
                <div className="mb-5 rounded-2xl border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="mb-5">
                <label className="block text-xs font-medium uppercase text-slate-600 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="test@gmail.com"
                  className="w-full bg-slate-50 border border-slate-300 rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                />
              </div>

              <div className="mb-7">
                <label className="block text-xs font-medium uppercase text-slate-600 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full bg-slate-50 border border-slate-300 rounded-2xl px-4 py-3 pr-10 text-sm focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-2xl bg-gradient-to-r from-indigo-500 via-sky-500 to-blue-500 py-3.5 text-sm font-semibold text-white shadow-lg hover:shadow-xl disabled:opacity-60"
              >
                {loading ? "Signing in..." : "Sign in"}
              </button>

              <p className="text-center text-xs text-slate-500 mt-6">
                © {new Date().getFullYear()} Sum Events
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;